import { useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormDialog } from "@/components/FormDialog";
import { api, errMsg, CERT_LABELS } from "@/lib/api";

const opt = (list, label = "name") => (list || []).map((x) => ({ value: x.id, label: x[label] }));

export function TenancyDialog({ open, onOpenChange, properties, units, presetProperty, onCreated }) {
  const fields = [
    { name: "property_id", label: "Property", type: "select", required: true, options: opt(properties), default: presetProperty, full: true },
    { name: "unit_id", label: "Unit / room (optional)", type: "select", nullable: true, noneLabel: "Whole property",
      options: (v) => opt((units || []).filter((u) => u.property_id === v.property_id)), full: true },
    { name: "renter_name", label: "Renter name", required: true },
    { name: "renter_email", label: "Renter email", type: "email", required: true, help: "Their welcome pack and login are sent here" },
    { name: "renter_phone", label: "Renter phone" },
    { name: "rent_frequency", label: "Rent frequency", type: "select", default: "monthly",
      options: [{ value: "monthly", label: "Monthly" }, { value: "weekly", label: "Weekly" }, { value: "quarterly", label: "Quarterly" }] },
    { name: "start_date", label: "Start date", type: "date", required: true },
    { name: "end_date", label: "End date (optional)", type: "date", nullable: true, help: "Leave empty for a rolling 12-month schedule" },
    { name: "rent_amount", label: "Rent amount", type: "number", required: true },
    { name: "deposit_amount", label: "Deposit", type: "number" },
    { name: "deposit_scheme", label: "Deposit scheme", placeholder: "e.g. DPS, TDS, mydeposits", full: true },
    { name: "send_welcome", label: "Send welcome pack with renter login", type: "checkbox", default: true, full: true },
  ];
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="New tenancy" testid="tenancy-form"
      description="A rent schedule is created automatically. The renter gets a welcome pack with their portal login."
      fields={fields} submitLabel="Create tenancy"
      onSubmit={async (v) => {
        const r = await api.post("/biz/tenancies", { ...v, origin_url: window.location.origin });
        toast.success("Tenancy created");
        onCreated?.(r.data);
      }} />
  );
}

export function WelcomeResultDialog({ welcome, onClose }) {
  if (!welcome) return null;
  const sent = welcome.email_status === "sent";
  const copy = () => {
    navigator.clipboard?.writeText(`Login: ${window.location.origin}/login\nEmail: ${welcome.email}\nTemporary password: ${welcome.temp_password}`);
    toast.success("Copied");
  };
  return (
    <Dialog open={!!welcome} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="welcome-result-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{sent ? "Welcome pack sent" : welcome.temp_password ? "Share these login details" : "Renter already has an account"}</DialogTitle>
          <DialogDescription data-testid="welcome-email-status">
            {!welcome.temp_password ? `${welcome.email} already has a renter login and can sign in with their existing password (or use "Forgot password").` : sent ? `Emailed to ${welcome.email}.` : welcome.email_status === "failed"
              ? "The email could not be sent (check SMTP settings with your administrator). Share these details with them securely."
              : "Email sending is not configured yet. Share these details with them securely."}
          </DialogDescription>
        </DialogHeader>
        {welcome.temp_password && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 font-mono text-sm space-y-1">
            <div>Email: <span data-testid="welcome-email">{welcome.email}</span></div>
            <div>Temporary password: <span data-testid="welcome-temp-password">{welcome.temp_password}</span></div>
          </div>
        )}
        {welcome.temp_password && <Button variant="outline" onClick={copy} data-testid="welcome-copy"><Copy className="w-4 h-4 mr-2" />Copy details</Button>}
      </DialogContent>
    </Dialog>
  );
}

export function CertDialog({ open, onOpenChange, properties, presetProperty, initial, onSaved }) {
  const fields = [
    { name: "property_id", label: "Property", type: "select", required: true, options: opt(properties), default: presetProperty, full: true },
    { name: "cert_type", label: "Certificate", type: "select", required: true, default: "gas_safety",
      options: Object.entries(CERT_LABELS).map(([value, label]) => ({ value, label })), full: true },
    { name: "issue_date", label: "Issue date", type: "date", nullable: true },
    { name: "expiry_date", label: "Expiry date", type: "date", required: true },
    { name: "reference", label: "Reference / certificate no.", full: true },
    { name: "notes", label: "Notes", type: "textarea" },
  ];
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={initial ? "Edit certificate" : "Add certificate"} testid="cert-form"
      fields={fields} initial={initial}
      onSubmit={async (v) => {
        if (initial) await api.put(`/biz/compliance/${initial.id}`, v);
        else await api.post("/biz/compliance", v);
        toast.success("Certificate saved");
        onSaved?.();
      }} />
  );
}

const DOC_CATS = ["lease", "certificate", "inventory", "invoice", "id", "other"];

export function UploadDialog({ open, onOpenChange, properties, tenancies, presetProperty, onSaved }) {
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ name: "", category: "lease", property_id: presetProperty || "none", tenancy_id: "none", shared: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", f.name);
    fd.append("category", f.category);
    fd.append("property_id", f.property_id === "none" ? "" : f.property_id);
    fd.append("tenancy_id", f.tenancy_id === "none" ? "" : f.tenancy_id);
    fd.append("shared_with_renter", f.shared ? "true" : "false");
    setBusy(true);
    try {
      await api.post("/biz/documents", fd);
      toast.success("Document uploaded");
      setFile(null);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const sel = (k, items, none) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger data-testid={`upload-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {none && <SelectItem value="none">{none}</SelectItem>}
        {items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upload-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Upload document</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>File (PDF, image, Word, Excel — max 15MB)</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file" /></div>
          <div className="space-y-1.5"><Label>Display name (optional)</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="upload-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label>{sel("category", DOC_CATS.map((c) => ({ value: c, label: c[0].toUpperCase() + c.slice(1) })))}</div>
            <div className="space-y-1.5"><Label>Property</Label>{sel("property_id", opt(properties), "Not linked")}</div>
          </div>
          <div className="space-y-1.5"><Label>Tenancy</Label>{sel("tenancy_id", opt(tenancies, "renter_name"), "Not linked")}</div>
          <div className="flex items-center gap-3"><Switch checked={f.shared} onCheckedChange={(c) => setF({ ...f, shared: c })} data-testid="upload-shared" /><Label>Visible to the renter in their portal</Label></div>
          <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MaintDialog({ open, onOpenChange, properties, presetProperty, onSaved }) {
  const fields = [
    { name: "property_id", label: "Property", type: "select", required: true, options: opt(properties), default: presetProperty, full: true },
    { name: "title", label: "Issue", required: true, full: true },
    { name: "priority", label: "Priority", type: "select", default: "medium",
      options: ["low", "medium", "high", "urgent"].map((p) => ({ value: p, label: p[0].toUpperCase() + p.slice(1) })) },
    { name: "description", label: "Details", type: "textarea" },
  ];
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="Log maintenance request" testid="maint-form" fields={fields}
      onSubmit={async (v) => { await api.post("/biz/maintenance", v); toast.success("Request logged"); onSaved?.(); }} />
  );
}
